import * as Sentry from '@sentry/react';
import { ComponentLoadingSpinner } from '@solinkcorp/solink-components';
import React, { Suspense } from 'react';
import { trackErrorBoundary } from 'src/core/analytics';
import ErrorFallback from 'src/core/components/ErrorBoundary/ErrorFallback';
import { IS_PRODUCTION } from 'src/environment';

const DEFAULT_ERROR_MESSAGE =
  'An unexpected error occurred. Please reload or retry the operation in a few minutes. If the problem persists, contact Solink support.';

function Loadable<T extends React.ComponentType<any>>(options: {
  loader: () => Promise<{ default: T }>;
  loading?: React.ComponentType<any>;
}): React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<T> & React.RefAttributes<T>> {
  const LazyComponent = React.lazy(options.loader);
  const Fallback = options.loading ?? ComponentLoadingSpinner;

  const Loader = React.forwardRef<T, React.ComponentPropsWithoutRef<T>>((props, ref) => {
    return (
      <LoadableErrorBoundary loader={options.loader}>
        <Suspense fallback={<Fallback />}>
          <LazyComponent {...(props as any)} ref={ref as any} />
        </Suspense>
      </LoadableErrorBoundary>
    );
  });
  Loader.displayName = `Loader`;

  return Loader as React.ForwardRefExoticComponent<
    React.ComponentPropsWithoutRef<T> & React.RefAttributes<T>
  >;
}

export default Loadable;

/**
 * Wraps a component - if the component or any of its descendants throws an error
 * while rendering, this will replace the wrapped component with a generic
 * error screen with reload option.
 */
class LoadableErrorBoundary extends React.Component<
  { children: React.ReactNode; loader: () => any },
  { error?: Error }
> {
  constructor(props: { children: React.ReactNode; loader: () => any }) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to Mixpanel and Intercom.
    trackErrorBoundary(error, errorInfo.componentStack, {
      loader: this.props.loader.toString(),
    });

    // Log error to Sentry
    if (IS_PRODUCTION) {
      Sentry.captureException(error, {
        tags: {
          error_type: 'sadface',
        },
        extra: {
          componentStack: errorInfo.componentStack,
          loader: this.props.loader.toString(),
        },
      });
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      return <ErrorFallback message={DEFAULT_ERROR_MESSAGE} />;
    }
    return this.props.children;
  }
}
